import { TimelinePaymentEvent } from '@/components/Timeline/types';
import { ActorAvatar } from '@/components/Timeline/TimelineItems/ActorAvatar';
import { DateTimeTooltip } from '@/components/Timeline/TimelineItems/DateTimeTooltip';
import { TimelineCard } from '@/components/Timeline/TimelineItems/TimelineCard';
import { formatDollars } from '@/services/money';
import { formatProcessor } from '@/services/payments';
import { Group, Stack, Text } from '@mantine/core';
import { ORBIT_SETTLED } from '@/theme/colors';

/**
 * Surfaces 02 and 03 — `$X paid via [method]` and `[Service] paid in full`.
 *
 * Append-only and header-only: these are immutable events, and that is what makes
 * the timeline a usable financial audit trail. Nothing here recomputes, so a
 * refund landing later never rewrites what a card said at the time.
 *
 * Restyled onto `TimelineCard` with a gutter marker alongside the order card
 * (BAR-2525) — the teal left stripe and the missing marker were the same two
 * cards' problem, and leaving one of them fixed would have made billing the only
 * group whose cards disagree with each other.
 *
 * The avatar distinguishes how the money got here: a processor webhook wrote it,
 * or a teammate keyed it by hand. `recordedBy` comes from the charge's
 * `Provenance`, which only a hand-recorded settlement has — so its **absence** is
 * what marks a card as system-written. Never inferred from the processor, which
 * would be wrong in both directions: Cherry arrives by webhook when the patient
 * pays the texted request, and by hand when they pay in the Cherry portal
 * (BAR-2526).
 */
export function PaymentEventCard({ item }: { item: TimelinePaymentEvent }) {
  const paidInFull = item.kind === 'paid-in-full';
  const processorLabel = formatProcessor(item.processor);

  const title = paidInFull
    ? `${item.serviceName ?? 'Treatment'} paid in full`
    : `${formatDollars(item.amount)} paid${processorLabel ? ` via ${processorLabel}` : ''}`;

  return (
    <TimelineCard
      header={
        <Group justify="space-between" align="center" wrap="nowrap">
          <Stack gap={0}>
            <Text size="md" fw={paidInFull ? 700 : 600} c={paidInFull ? ORBIT_SETTLED : undefined}>
              {title}
            </Text>
            {item.payerName && (
              <Text size="xs" c="dimmed">
                {item.payerName}
              </Text>
            )}
          </Stack>
          <Group gap="md" align="center" wrap="nowrap">
            {item.processorRefId && (
              <Text size="xs" c="dimmed">
                {item.processorRefId}
              </Text>
            )}
            {item.recordedBy ? (
              <ActorAvatar variant="teammate" label={`Recorded by ${item.recordedBy}`} name={item.recordedBy} />
            ) : (
              <ActorAvatar variant="system" label="Recorded automatically by the payment processor" />
            )}
            <DateTimeTooltip date={item.timestamp} />
          </Group>
        </Group>
      }
    >
      {null}
    </TimelineCard>
  );
}
